/**
 * Обновление без пересборки (только в оболочке телефона).
 *
 * Приложение несёт свою копию интерфейса (мгновенный старт, работает без сети),
 * а при запуске и при возврате на экран тихо проверяет {APP_URL}/ota/latest.json
 * = { version, url, minNative }. Если версия новее и нативная сборка достаточно
 * свежая (minNative ≤ номера сборки), пакет скачивается и включается при
 * СЛЕДУЮЩЕМ запуске (`next`) — не посреди работы человека.
 *
 * Нативная часть (уведомления, камера) этим путём не обновляется — только
 * пересборкой. Поэтому minNative: пакет, которому нужен более новый нативный
 * слой, на старые установки не едет.
 *
 * Всё обёрнуто в try/catch: сбой обновления не должен мешать работе.
 */
import config from './config';
import { isNative } from './platform';
import { OTA_VERSION } from './buildInfo';

export interface OtaManifest {
  version: string;
  url: string;
  minNative?: number;
}

/** Нужно ли ставить пакет: версия отличается и нативная сборка подходит. */
export function shouldInstall(m: OtaManifest, currentVersion: string, nativeBuild: number): boolean {
  if (!m.version || !m.url) return false;
  if (m.version === currentVersion) return false;
  if (typeof m.minNative === 'number' && m.minNative > nativeBuild) return false;
  return true;
}

async function nativeBuildNumber(): Promise<number> {
  try {
    const { App } = await import('@capacitor/app');
    const info = await App.getInfo();
    const n = parseInt(info.build, 10);
    return Number.isFinite(n) ? n : 0;
  } catch {
    return 0;
  }
}

/** Версия пакета, который выполняется сейчас («встроенная» = версия сборки). */
export async function getBundleVersion(): Promise<string> {
  if (!isNative()) return OTA_VERSION;
  try {
    const { CapacitorUpdater } = await import('@capgo/capacitor-updater');
    const { bundle } = await CapacitorUpdater.current();
    return !bundle.version || bundle.version === 'builtin' ? OTA_VERSION : bundle.version;
  } catch {
    return OTA_VERSION;
  }
}

let идёт = false;

export async function checkForUpdate(): Promise<void> {
  if (идёт) return;
  идёт = true;
  try {
    const res = await fetch(`${config.APP_URL}/ota/latest.json`, { cache: 'no-store' });
    if (!res.ok) return;
    const manifest = (await res.json()) as OtaManifest;
    const current = await getBundleVersion();
    if (!shouldInstall(manifest, current, await nativeBuildNumber())) return;
    const { CapacitorUpdater } = await import('@capgo/capacitor-updater');
    const bundle = await CapacitorUpdater.download({ url: manifest.url, version: manifest.version });
    await CapacitorUpdater.next({ id: bundle.id });
  } catch { /* нет сети или пакет битый — попробуем при следующем запуске */ }
  finally { идёт = false; }
}

/** Вызвать один раз при старте. Возвращает функцию отключения слушателя. */
export async function initLiveUpdate(): Promise<void> {
  if (!isNative()) return;
  try {
    const { CapacitorUpdater } = await import('@capgo/capacitor-updater');
    // Сообщаем плагину, что пакет запустился. Без этого он сочтёт запуск
    // неудачным и откатится на прошлый пакет.
    await CapacitorUpdater.notifyAppReady();
  } catch { /* плагина нет */ }
  void checkForUpdate();
  try {
    const { App } = await import('@capacitor/app');
    void App.addListener('resume', () => { void checkForUpdate(); });
  } catch { /* без плагина — только при старте */ }
}
